import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const EMPTY = {
  hospitalName: '', address: '', phone: '', email: '',
  licenseNo: '', gstNo: '', taxPct: '0', billHeader: '', billFooter: '',
};

const inp = { className: 'form-control' };

function F({ label, children, hint }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: '1px dashed #ccc', margin: '6px 0' }} />;
}

export default function HospitalSetup() {
  const [tab, setTab]       = useState('info');
  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [err, setErr]       = useState('');

  const load = useCallback(() => {
    hospitalApi.getInfo().then(d => {
      if (d && Object.keys(d).length > 0) {
        setForm({
          hospitalName: d.hospital_name || '',
          address:      d.address       || '',
          phone:        d.phone         || '',
          email:        d.email         || '',
          licenseNo:    d.license_no    || '',
          gstNo:        d.gst_no        || '',
          taxPct:       d.tax_pct != null ? String(d.tax_pct) : '0',
          billHeader:   d.bill_header   || '',
          billFooter:   d.bill_footer   || '',
        });
      }
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      await hospitalApi.upsertInfo({
        ...form,
        taxPct: parseFloat(form.taxPct) || 0,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const taxAmt = ((100 * (parseFloat(form.taxPct) || 0)) / 100).toFixed(2);
  const preTotal = (100 + parseFloat(taxAmt)).toFixed(2);

  const C = '#1b6ca8';
  const DARK = '#0f4c75';
  const LIGHT = '#e8f4fd';

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'info', label: '🏥 Hospital Info' },
          { key: 'bill', label: '🧾 Bill Settings' },
        ].map(t => (
          <button key={t.key} className={`tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">
            {tab === 'info' ? 'Hospital Information' : 'Bill & Tax Settings'}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {saved && <span style={{ color: '#27ae60', fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '6px 18px' }}
              onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Settings'}
            </button>
          </div>
        </div>

        <div className="card-body">
          {err && (
            <div style={{ color: '#c5221f', background: '#fce8e6', border: '1px solid #f5c6c6',
              borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>
          )}

          {/* ── Hospital Info ── */}
          {tab === 'info' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
              <F label="Hospital Name *">
                <input {...inp} value={form.hospitalName} placeholder="e.g. City General Hospital"
                  onChange={e => setForm(f => ({ ...f, hospitalName: e.target.value }))} />
              </F>
              <F label="Phone">
                <input {...inp} value={form.phone} placeholder="+91 98765 43210"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </F>
              <F label="Email">
                <input {...inp} type="email" value={form.email} placeholder="info@hospital.com"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </F>
              <F label="License No">
                <input {...inp} value={form.licenseNo} placeholder="Hospital registration number"
                  onChange={e => setForm(f => ({ ...f, licenseNo: e.target.value }))} />
              </F>
              <F label="GST No">
                <input {...inp} value={form.gstNo} placeholder="27AAAAA0000A1Z5"
                  onChange={e => setForm(f => ({ ...f, gstNo: e.target.value }))} />
              </F>
              <F label="Address">
                <textarea {...inp} rows={3} value={form.address}
                  placeholder="Full address including city, state, PIN"
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  style={{ resize: 'vertical' }} />
              </F>
            </div>
          )}

          {/* ── Bill Settings ── */}
          {tab === 'bill' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              <F label="Tax Percentage (%)"
                hint="Applied to all invoices automatically.">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input {...inp} type="number" min={0} max={100} step={0.5}
                    style={{ maxWidth: 120 }} value={form.taxPct}
                    onChange={e => setForm(f => ({ ...f, taxPct: e.target.value }))} />
                  <span style={{ fontSize: 14, color: '#5f6368' }}>%</span>
                  <span style={{ background: LIGHT, color: C, borderRadius: 8,
                    padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                    On ₹100 → Tax ₹{(parseFloat(form.taxPct) || 0).toFixed(2)}
                  </span>
                </div>
              </F>
              <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <F label="Bill Header Text" hint="Printed at the top of every invoice.">
                  <textarea {...inp} rows={3} value={form.billHeader}
                    placeholder={'e.g.\nCity General Hospital\nQuality Healthcare'}
                    onChange={e => setForm(f => ({ ...f, billHeader: e.target.value }))}
                    style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                </F>
                <F label="Bill Footer Text" hint="Printed at the bottom of every invoice.">
                  <textarea {...inp} rows={3} value={form.billFooter}
                    placeholder={'e.g.\nThank you for trusting us!\nGet well soon.'}
                    onChange={e => setForm(f => ({ ...f, billFooter: e.target.value }))}
                    style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                </F>
              </div>

              {/* Bill Preview */}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 8 }}>🧾 Bill Preview</div>
                <div style={{ background: '#fff', border: '1px dashed #ccc', borderRadius: 8,
                  padding: '16px', fontFamily: 'monospace', fontSize: 12.5, maxWidth: 340,
                  lineHeight: 1.7, color: '#202124' }}>
                  <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 14, color: DARK }}>
                    {form.hospitalName || 'Hospital Name'}
                  </div>
                  {form.address && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>{form.address}</div>
                  )}
                  {form.phone && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>📞 {form.phone}</div>
                  )}
                  {form.licenseNo && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>Lic: {form.licenseNo}</div>
                  )}
                  {form.gstNo && (
                    <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>GST: {form.gstNo}</div>
                  )}
                  {form.billHeader && (
                    <>
                      <Divider />
                      <div style={{ textAlign: 'center', color: C, fontSize: 12 }}>{form.billHeader}</div>
                    </>
                  )}
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888' }}>
                    <span>Invoice: INV-XXXX</span>
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>Date: {new Date().toLocaleDateString('en-IN')}</div>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Consultation Fee</span><span>₹100</span>
                  </div>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Subtotal</span><span>₹100</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tax ({form.taxPct || 0}%)</span>
                    <span>₹{taxAmt}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 14, marginTop: 4 }}>
                    <span>TOTAL</span><span>₹{preTotal}</span>
                  </div>
                  {form.billFooter && (
                    <>
                      <Divider />
                      <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>{form.billFooter}</div>
                    </>
                  )}
                  <div style={{ textAlign: 'center', fontSize: 9, color: '#aaa', marginTop: 6 }}>
                    Powered by SmaBizZ
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
